import React from "react";
import ReactDOM from "react-dom/client";
import { Capacitor } from "@capacitor/core";
import { App } from "./App";
import "./theme/styles.css";

// Register the PWA service worker on the web build only. Inside Capacitor
// (iOS / Android WebView) service workers are unsupported and registering
// one would either no-op noisily or interfere with native networking, so
// we skip. The SW itself is a near-no-op today; see /public/sw.js.
//
// Update flow (see /public/sw.js for the SW side):
//   1. version-sw.mjs stamps dist/sw.js with a unique build version,
//      so each deploy changes the file's bytes.
//   2. Browser fetches /sw.js (no-cache header in _headers ensures the
//      fetch isn't skipped) on register + periodically.
//   3. New bytes → install → skipWaiting → activate → clients.claim.
//   4. `controllerchange` fires once on the active page, and we reload
//      it so the new JS bundle actually starts running. Without the
//      reload, the SW would now control fetches but the page would
//      still be executing last week's code.
if (
  "serviceWorker" in navigator &&
  !Capacitor.isNativePlatform() &&
  import.meta.env.PROD
) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then((reg) => {
      // Nudge the browser to check for updates whenever the user
      // returns to the tab — covers the common case of leaving a PWA
      // open for days.
      const recheck = () => { reg.update().catch(() => {}); };
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") recheck();
      });
    }).catch(() => {
      /* SW register failed — app still works without it */
    });

    // Reload exactly once when a NEW SW takes control on an already-
    // controlled page. We deliberately only attach this listener if a
    // controller is already present — otherwise `controllerchange`
    // also fires on the very first SW install (no SW → first SW),
    // which would reload first-time visitors for no reason. The
    // `refreshing` guard stops the rare double-reload that can happen
    // if a tab is open during back-to-back deploys.
    if (navigator.serviceWorker.controller) {
      let refreshing = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });
    }
  });
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
